import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Business, Save, StorefrontOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/PageHeader';
import { useAppDispatch, useAppSelector } from '../../app/store';
import { useCan } from '../../core/authorization/PermissionProvider';
import { reloadSessionData } from '../../app/sessionSlice';
import { platformApi } from '../../core/api/client';
import { BusinessesGrid } from '../billing/BillingPages';

// Borderless surface with a soft drop shadow, matching the Hub cards.
const ORG_CARD_SX = {
  mb: 2,
  borderRadius: '20px',
  boxShadow: 'var(--shadow-card, 0 1px 2px rgba(15,23,42,.04), 0 8px 24px rgba(15,23,42,.06))',
} as const;

const LANGUAGES = ['al', 'en', 'it', 'de', 'fr'] as const;

/**
 * The organization editor: the tenant's identity and contact.
 *
 * The organization is the tenant. Everything that differs per business — the public profile,
 * time zone and currency, VAT and bank percentage, the restaurant menu key, license and working
 * hours — lives on the organization profiles (one per product, PlatformDB 056), edited at
 * /app/organization-profiles.
 */
export function OrganizationPage() {
  const dispatch = useAppDispatch();
  const organization = useAppSelector((s) => s.session.organization);
  const canManage = useCan('platform.organization', 'update');

  const [form, setForm] = useState({
    name: organization.name,
    registrationNumber: organization.registrationNumber ?? '',
    email: organization.email ?? '',
    phone: organization.phone ?? '',
    address: organization.address ?? '',
    location: organization.location ?? '',
    description: organization.description ?? '',
    website: organization.website ?? '',
    fax: organization.fax ?? '',
    languageCode: organization.languageCode ?? '',
  });
  const [saving, setSaving] = useState(false);

  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: event.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await platformApi.updateOrganization({
        name: form.name.trim(),
        registrationNumber: form.registrationNumber.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        location: form.location.trim() || null,
        description: form.description.trim() || null,
        website: form.website.trim() || null,
        fax: form.fax.trim() || null,
        languageCode: form.languageCode.trim() || null,
      });
      await dispatch(reloadSessionData()).unwrap();
      toast.success('Organization settings saved.');
    } catch {
      // Error toast raised by the HTTP layer.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1080, mx: 'auto' }}>
      <PageHeader title="Organization" />
      <Box component="section" sx={{ mb: 4 }} aria-labelledby="org-businesses">
        <Typography id="org-businesses" variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Your businesses</Typography>
        <BusinessesGrid />
      </Box>
      {!canManage ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          You can view these settings, but your role does not allow editing them.
        </Alert>
      ) : null}

      <Box component="form" onSubmit={(e) => { e.preventDefault(); void handleSave(); }}>
        <Card elevation={0} sx={ORG_CARD_SX}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Identity and contact</Typography>
            <Stack spacing={2} maxWidth={640}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Code" value={organization.code} disabled helperText="The organization code cannot be changed" fullWidth />
                <TextField label="Name" value={form.name} onChange={set('name')} required fullWidth disabled={!canManage} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Registration number" value={form.registrationNumber} onChange={set('registrationNumber')} fullWidth disabled={!canManage} />
                <TextField label="Email" type="email" value={form.email} onChange={set('email')} fullWidth disabled={!canManage} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Phone" value={form.phone} onChange={set('phone')} fullWidth disabled={!canManage} />
                <TextField label="Fax" value={form.fax} onChange={set('fax')} fullWidth disabled={!canManage} />
              </Stack>
              <TextField label="Address" value={form.address} onChange={set('address')} fullWidth disabled={!canManage} />
              <TextField label="Location" value={form.location} onChange={set('location')} fullWidth disabled={!canManage} helperText="Shown on receipts and on the organization's public page" />
              <TextField label="Description" value={form.description} onChange={set('description')} fullWidth multiline minRows={2} disabled={!canManage} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Website" value={form.website} onChange={set('website')} fullWidth disabled={!canManage} />
                <TextField label="Language" select value={form.languageCode} onChange={set('languageCode')} fullWidth disabled={!canManage}>
                  <MenuItem value="">Not set</MenuItem>
                  {LANGUAGES.map((code) => (
                    <MenuItem key={code} value={code}>{code}</MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ ...ORG_CARD_SX, mb: 0 }}>
          <CardContent>
            <Typography variant="subtitle2">Business settings</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5, maxWidth: 640 }}>
              Time zone, currency, VAT, bank percentage, menu key, license, working hours and the public directory
              listing are set for each business on its organization profile.
            </Typography>
            <Button component={Link} to="/app/organization-profiles" variant="outlined" startIcon={<StorefrontOutlined />}>
              Organization profiles
            </Button>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <Button type="submit" variant="contained" startIcon={<Save />} disabled={!canManage || saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          <Business sx={{ color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary">
            Organization id: {organization.id}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
